/**
 * Unified API & WebSocket base URL configuration.
 *
 * Why this exists:
 *   The web app today uses relative paths ("/api/...") that resolve against
 *   window.location.origin. That works perfectly in the browser but will fail
 *   inside a future native shell (e.g. Capacitor) where the app is loaded from
 *   capacitor://localhost or https://localhost — there is no co-located server.
 *
 * Contract:
 *   - When VITE_API_BASE_URL is unset / empty (current web build),
 *     buildApiUrl(path) returns the path UNCHANGED → fetch stays relative
 *     and the existing web behavior is byte-identical to before.
 *   - When VITE_API_BASE_URL is set (future native build), it is prefixed.
 *   - Same logic applies to VITE_WS_URL for the WebSocket connection:
 *       unset → derive ws/wss from window.location (existing behavior),
 *       set   → use the provided absolute ws(s):// URL.
 *
 * This file MUST stay free of side effects and free of business logic.
 */

const RAW_API_BASE = (
  (import.meta.env.VITE_API_BASE_URL ?? "") as string
).toString().trim();

const RAW_WS_BASE = (
  (import.meta.env.VITE_WS_URL ?? "") as string
).toString().trim();

// Strip trailing slashes so concatenation is safe: `${BASE}${"/api/..."}`.
export const API_BASE_URL = RAW_API_BASE.replace(/\/+$/, "");
export const WS_BASE_URL = RAW_WS_BASE.replace(/\/+$/, "");

// On the native shell (Capacitor) the app loads from capacitor://localhost with
// NO co-located server, so a relative "/api/..." resolves to nothing and the
// app shows no products/stores. If a native build didn't inject
// VITE_API_BASE_URL, fall back to the public backend origin instead of staying
// relative — this keeps the native app working regardless of build-time env.
const NATIVE_FALLBACK_ORIGIN = "https://iraq-sooq.com";

function effectiveApiBase(): string {
  if (API_BASE_URL) return API_BASE_URL;
  return isNativePlatform() ? NATIVE_FALLBACK_ORIGIN : "";
}

/**
 * Compose an HTTP(S) URL for an API call.
 *
 * - Empty path → returned unchanged.
 * - Already-absolute URLs (http:// or https://) → returned unchanged.
 * - No API_BASE_URL configured → returned unchanged (relative — web fallback).
 * - API_BASE_URL configured → prefixed; the leading "/" is normalized.
 */
export function buildApiUrl(path: string): string {
  if (!path) return path;
  if (/^https?:\/\//i.test(path)) return path;
  const base = effectiveApiBase();
  if (!base) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalized}`;
}

/**
 * Compose a public app URL (used for links that must work on any device,
 * e.g. delivery-confirm QR / share / copy links handed to the courier).
 *
 * - On the web, `window.location.origin` is already the public origin,
 *   so we keep the existing behavior unchanged.
 * - On the native shell (Capacitor) `window.location.origin` is
 *   `https://localhost` (or `capacitor://localhost`) — useless when shared
 *   with anyone outside the app. There we fall back to API_BASE_URL,
 *   which is configured at build time to the public backend origin
 *   (e.g. https://iraq-sooq.com).
 *
 * Always returns an origin without a trailing slash; the caller appends
 * the path (`${buildPublicAppUrl()}/delivery-confirm?...`).
 */
export function buildPublicAppUrl(): string {
  if (typeof window !== "undefined") {
    const origin = window.location?.origin || "";
    const isLocalhostOrigin = /^(https?|capacitor):\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(origin);
    if (origin && !isLocalhostOrigin) return origin.replace(/\/+$/, "");
  }
  const base = effectiveApiBase();
  if (base) return base;
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, "");
  }
  return "";
}

/**
 * Compose a WebSocket URL.
 *
 * - WS_BASE_URL configured → used directly + path appended.
 * - Otherwise, derive ws/wss + host from window.location (existing behavior).
 */
export function buildWsUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (WS_BASE_URL) {
    return `${WS_BASE_URL}${normalized}`;
  }
  // Native shell: derive from the public backend, not capacitor://localhost.
  if (isNativePlatform()) {
    return `${NATIVE_FALLBACK_ORIGIN.replace(/^http/i, "ws")}${normalized}`;
  }
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}${normalized}`;
}

// ─── Optional Bearer-token storage (for native app clients) ─────────────────
//
// Why this exists:
//   The web build authenticates via the existing httpOnly cookie session and
//   never writes anything here — `getAuthToken()` simply returns null on the
//   web, so all helpers below become no-ops. The native shell (Capacitor)
//   will later call `setAuthToken(...)` once after login to opt into
//   Authorization: Bearer auth, and `clearAuthToken()` on logout.
//
// Storage choice:
//   - Sync surface stays on localStorage (fast, browser-standard, used by
//     the rest of the code without await).
//   - On Capacitor, the value is ALSO mirrored to `@capacitor/preferences`
//     which is backed by:
//       * iOS:     UserDefaults
//       * Android: SharedPreferences
//     These are guaranteed to persist across app restarts and OS upgrades.
//     Pure-localStorage on iOS WKWebView under the `capacitor://localhost`
//     scheme is unreliable — the user reported having to re-enter
//     credentials on every iOS launch, while the same code worked on
//     Android because `https://localhost` localStorage is durable there.
//   - On app boot, `hydrateAuthTokenFromNativeStorage()` (called from
//     main.tsx) reads the Preferences value back into localStorage so
//     the sync getter just works on the first render.

const TOKEN_STORAGE_KEY = "auth_token";

function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

// Lazily-imported so this module stays usable in the web build without
// pulling the Capacitor runtime. The import resolves to a no-op module
// shape if the plugin isn't installed.
async function loadPreferences(): Promise<typeof import("@capacitor/preferences") | null> {
  try {
    const mod = await import("@capacitor/preferences");
    return mod;
  } catch {
    return null;
  }
}

function isNativePlatform(): boolean {
  try {
    // Avoid bundler resolution on the web by guarding behind a runtime check.
    const cap = (window as any).Capacitor;
    return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
  } catch {
    return false;
  }
}

/** Returns the stored auth token, or `null` on the web (and on any failure). */
export function getAuthToken(): string | null {
  const s = safeStorage();
  if (!s) return null;
  try {
    const v = s.getItem(TOKEN_STORAGE_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

/** Persist a token. No-op when storage is unavailable.
 *  On native, also mirrors into Capacitor Preferences (UserDefaults /
 *  SharedPreferences) for cross-launch durability. */
export function setAuthToken(token: string): void {
  if (!token) return;
  const s = safeStorage();
  if (s) {
    try { s.setItem(TOKEN_STORAGE_KEY, token); } catch {}
  }
  if (isNativePlatform()) {
    loadPreferences().then(mod => {
      if (mod) {
        mod.Preferences.set({ key: TOKEN_STORAGE_KEY, value: token }).catch(() => {});
      }
    });
  }
}

/** Remove any persisted token. No-op when nothing is stored. */
export function clearAuthToken(): void {
  const s = safeStorage();
  if (s) {
    try { s.removeItem(TOKEN_STORAGE_KEY); } catch {}
  }
  if (isNativePlatform()) {
    loadPreferences().then(mod => {
      if (mod) {
        mod.Preferences.remove({ key: TOKEN_STORAGE_KEY }).catch(() => {});
      }
    });
  }
}

/** Run once at app boot on Capacitor: copy the Preferences value into
 *  localStorage so the synchronous `getAuthToken()` reflects the
 *  persisted token on the very first render. Resolves immediately on
 *  the web. Safe to call multiple times. */
export async function hydrateAuthTokenFromNativeStorage(): Promise<void> {
  if (!isNativePlatform()) return;
  const mod = await loadPreferences();
  if (!mod) return;
  try {
    const { value } = await mod.Preferences.get({ key: TOKEN_STORAGE_KEY });
    if (value && value.length > 0) {
      const s = safeStorage();
      if (s) {
        try { s.setItem(TOKEN_STORAGE_KEY, value); } catch {}
      }
    }
  } catch {
    // ignore — degrade to "no token" which is the web default
  }
}
